/**
 * Servicio API para `OnboardingAsset` (backend admin endpoint).
 *
 * El catálogo estático (slug + título) vive en JSON versionado en el
 * repo. Este servicio expone los ficheros/URLs que se suben en
 * producción vía Refine admin. El frontend fusiona ambos en las
 * tarjetas: si el slug tiene una entrada en BD, usa `url` / `video_url`
 * de la respuesta; si no, pinta "Pendiente de subir".
 */

import apiClient from "./apiClient";

export type OnboardingAssetKind = "document" | "video";

export interface OnboardingAsset {
  id: number;
  slug: string;
  title: string;
  kind: OnboardingAssetKind;
  file: string | null;
  /** URL efectiva a servir al frontend. El backend firma con SigV4 si
   *  el asset es un `file` o un `video_s3_key`. Null si el asset es un
   *  vídeo externo (en cuyo caso usa `video_url`). */
  url: string | null;
  /** URL de embed externo (YouTube, Vimeo, CloudFront público). */
  video_url: string | null;
  /** Key S3 privada relativa a `onboarding_assets/`, p.ej.
   *  `videos/procesado-muestras-hoja.mp4`. El backend la firma al
   *  responder — nunca llega al frontend en crudo. */
  video_s3_key: string | null;
  size_bytes: number | null;
  uploaded_by: number | null;
  uploaded_by_name: string | null;
  uploaded_at: string;
  created_at: string;
}

// Registrado en `apps.api.urls` → prefijo `/api/`. NO va bajo /v1/
// (que es un router separado, `apps.api.v1.urls`).
const BASE = "/admin/onboarding-assets";

export const onboardingAssetService = {
  /** Lista todos los assets subidos (sin paginación). */
  async list(): Promise<OnboardingAsset[]> {
    const { data } = await apiClient.get<OnboardingAsset[]>(`${BASE}/`);
    return data;
  },

  /** Retrieval por slug — devuelve null si no existe (404). */
  async getBySlug(slug: string): Promise<OnboardingAsset | null> {
    try {
      const { data } = await apiClient.get<OnboardingAsset>(
        `${BASE}/by-slug/${slug}/`,
      );
      return data;
    } catch (e: unknown) {
      const err = e as { response?: { status?: number } };
      if (err.response?.status === 404) return null;
      throw e;
    }
  },

  /**
   * Sube o actualiza un documento (PDF). Si `existingId` viene se hace
   * PATCH; si no, POST (crea). El fichero va como multipart/form-data
   * — axios detecta FormData y ajusta el header automáticamente.
   */
  async uploadDocument(params: {
    slug: string;
    title: string;
    file: File;
    existingId?: number;
  }): Promise<OnboardingAsset> {
    const form = new FormData();
    form.append("slug", params.slug);
    form.append("title", params.title);
    form.append("kind", "document");
    form.append("file", params.file);

    if (params.existingId) {
      const { data } = await apiClient.patch<OnboardingAsset>(
        `${BASE}/${params.existingId}/`,
        form,
      );
      return data;
    }
    const { data } = await apiClient.post<OnboardingAsset>(`${BASE}/`, form);
    return data;
  },

  /** Guarda o actualiza una URL de embed EXTERNA (YouTube / Vimeo /
   *  CloudFront público) para un vídeo. Limpia `video_s3_key` para
   *  no dejar dos fuentes contradictorias. */
  async saveVideoUrl(params: {
    slug: string;
    title: string;
    videoUrl: string;
    existingId?: number;
  }): Promise<OnboardingAsset> {
    const body = {
      slug: params.slug,
      title: params.title,
      kind: "video" as const,
      video_url: params.videoUrl,
      video_s3_key: null,
    };
    if (params.existingId) {
      const { data } = await apiClient.patch<OnboardingAsset>(
        `${BASE}/${params.existingId}/`,
        body,
      );
      return data;
    }
    const { data } = await apiClient.post<OnboardingAsset>(`${BASE}/`, body);
    return data;
  },

  /** Guarda o actualiza la ruta S3 privada de un vídeo. El fichero
   *  debe estar previamente subido con `aws s3 cp` bajo
   *  `s3://<bucket>/onboarding_assets/<key>` — aquí sólo persiste la
   *  key relativa (sin el prefijo `onboarding_assets/`). El backend la
   *  firma con SigV4 cuando lista assets. Limpia `video_url` para
   *  evitar dos fuentes. */
  async saveVideoS3Key(params: {
    slug: string;
    title: string;
    s3Key: string;
    existingId?: number;
  }): Promise<OnboardingAsset> {
    const body = {
      slug: params.slug,
      title: params.title,
      kind: "video" as const,
      video_s3_key: params.s3Key,
      video_url: null,
    };
    if (params.existingId) {
      const { data } = await apiClient.patch<OnboardingAsset>(
        `${BASE}/${params.existingId}/`,
        body,
      );
      return data;
    }
    const { data } = await apiClient.post<OnboardingAsset>(`${BASE}/`, body);
    return data;
  },

  /** Elimina un asset (útil para revertir una subida errónea). */
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/${id}/`);
  },
};
